import { cn } from 'component-library';
import { isEqual } from 'lodash';

import { FormControl, FormItem } from '@/components/Form';
import { ButtonWidget, ButtonWidgetProps } from '@/components/widgets/a/Button/Button';

import { useControlsContext } from './Form';

type FormButtonProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formName: any;
  preventMultipleSelection?: boolean;
} & ButtonWidgetProps;

export const FormButtonItem = ({
  value,
  formName,
  className,
  preventMultipleSelection = false,
  ...delegated
}: FormButtonProps) => {
  const { setValue, watch } = useControlsContext();

  const selectedFormValue = watch(formName);
  const isArrayFormData = Array.isArray(selectedFormValue);

  const hasId = typeof value === 'object' && 'id' in value;

  const getIsSelected = () => {
    if (isArrayFormData) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return hasId ? selectedFormValue.some((v: any) => v.id === value.id) : selectedFormValue.includes(value);
    }

    if (hasId) {
      return selectedFormValue?.id === value.id;
    }

    return selectedFormValue === value;
  };

  const handleClick = () => {
    let newValue = value;

    if (isArrayFormData && !preventMultipleSelection) {
      const selectionWithoutCurrent = hasId
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          selectedFormValue.filter((v: any) => v.id !== value.id)
        : // eslint-disable-next-line @typescript-eslint/no-explicit-any
          selectedFormValue.filter((v: any) => v !== value);
      newValue = getIsSelected() ? selectionWithoutCurrent : [...selectedFormValue, value];
    }

    if (isArrayFormData && preventMultipleSelection) {
      newValue = [value];
    }

    setValue(formName, newValue);
  };

  const isSelected = getIsSelected();

  return (
    <FormItem>
      <FormControl>
        <ButtonWidget
          className={cn(
            'text-text-primary border-neutral-black-30',
            className,
            isSelected ? 'bg-secondary-12 border-text-primary' : '',
          )}
          onClick={handleClick}
          {...delegated}
        />
      </FormControl>
    </FormItem>
  );
};

export const SubmitButton = ({ shouldSubmit, ...props }: ButtonWidgetProps & { shouldSubmit?: boolean }) => {
  const { getValues, formState } = useControlsContext();

  const values = getValues();
  const isFormEmpty = Object.values(values).every(
    (value) => value === undefined || value === null || value === '' || value?.length === 0,
  );
  const isFormDirty = !isEqual(values, formState.defaultValues);

  return <ButtonWidget type='submit' disabled={(isFormEmpty || !isFormDirty) && !shouldSubmit} {...props} />;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ClearFormButton = ({ resetValues, ...props }: { resetValues?: any } & ButtonWidgetProps) => {
  const { getValues, reset, formState } = useControlsContext();

  const formCurrentValues = getValues();

  const hasFormDefaultValues = isEqual(formCurrentValues, formState.defaultValues);
  const hasFormResetValues = resetValues && isEqual(formCurrentValues, resetValues);

  const disableButton = resetValues ? hasFormResetValues : hasFormDefaultValues;

  const handleReset = () => {
    if (resetValues) {
      reset(resetValues);
    } else {
      reset();
    }
  };

  return <ButtonWidget {...props} onClick={handleReset} disabled={disableButton} />;
};
